import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { StatusBadge } from './StatusBadge';
import {
  formatDate,
  calculateDaysRemaining,
  getRenewalStatus,
  getRenewalStatusText,
  getRenewalStatusColor,
  getRenewalStatusIcon,
  getRenewalActionRecommendation
} from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Download,
  RefreshCw,
  AlertTriangle,
  Info,
  Server,
  Calendar,
  Clock,
  MoreVertical,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { certificatesAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { RenewalProcessDialog } from './RenewalProcessDialog';
// import { Progress } from "./ui/progress"; 

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
};

export function CertificateTable({ certificates, onRefresh }) {
  const [loading, setLoading] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, certId: null, type: null });
  const [infoDialog, setInfoDialog] = useState({ open: false, cert: null });
  const [renewalDialog, setRenewalDialog] = useState({ open: false, domain: '', type: '', status: 'processing' });

  // Sorting & Pagination State
  const [sortConfig, setSortConfig] = useState({ key: 'days_remaining', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Default 10 items per page

  // Logic Sorting
  const sortedCertificates = React.useMemo(() => {
    let sortableItems = [...certificates];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'days_remaining') {
          aValue = calculateDaysRemaining(a.expiration_date);
          bValue = calculateDaysRemaining(b.expiration_date);
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [certificates, sortConfig]);

  // Logic Pagination
  const totalPages = Math.ceil(sortedCertificates.length / itemsPerPage);
  const paginatedCertificates = sortedCertificates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleDownload = async (certId, domain) => {
    // Open dialog immediately
    setRenewalDialog({ open: true, domain, type: 'download', status: 'processing' });
    setLoading(prev => ({ ...prev, [`download-${certId}`]: true }));

    try {
      // Artificial delay to let the initial "preparation" logs show comfortably
      // This matches the visual experience the user requested
      await new Promise(resolve => setTimeout(resolve, 2000));

      const response = await certificatesAPI.download(certId);

      // Update dialog to success
      setRenewalDialog(prev => ({ ...prev, status: 'success' }));

      // Process file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Add current date to filename
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      link.setAttribute('download', `${domain}-certificates-${dateStr}.zip`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      // Additional small delay before closing dialog
      setTimeout(() => {
        setRenewalDialog({ open: false, domain: '', type: '', status: 'processing' });
      }, 2500);

    } catch (error) {
      // Update dialog to error status
      setRenewalDialog(prev => ({ ...prev, status: 'error' }));

      // Enhanced error notification (keep toast for persistence if dialog closes)
      const errorMsg = error.response?.data?.error || error.message || 'Gagal mengunduh sertifikat';
      const errorCode = error.response?.status;

      // Auto-close dialog after delay
      setTimeout(() => {
        setRenewalDialog({ open: false, domain: '', type: '', status: 'processing' });
      }, 3000);

      toast.error(
        <div className="flex flex-col gap-1">
          <div className="font-semibold">❌ Unduhan Gagal</div>
          <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
          <div className="text-xs text-muted-foreground mt-1">
            <div>Error: {errorMsg}</div>
            {errorCode && <div>Kode: {errorCode}</div>}
          </div>
        </div>,
        { duration: 6000 }
      );
    } finally {
      setLoading(prev => ({ ...prev, [`download-${certId}`]: false }));
    }
  };

  const handleRenewDryRun = async (certId, domain) => {
    // Show the renewal process dialog
    setRenewalDialog({ open: true, domain, type: 'dry-run', status: 'processing' });
    setLoading(prev => ({ ...prev, [`renew-${certId}`]: true }));

    try {
      const response = await certificatesAPI.renewDryRun(certId);

      // Update dialog to success status
      setRenewalDialog(prev => ({ ...prev, status: 'success' }));

      if (response.data.success) {
        // Enhanced success notification
        toast.success(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">✅ Simulasi Pembaruan Berhasil!</div>
            <div className="text-sm">
              Domain: <span className="font-medium">{domain}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {response.data.message || 'Sertifikat dapat diperbarui tanpa masalah. Anda dapat melanjutkan dengan Pembaruan Live.'}
            </div>
          </div>,
          { duration: 5000 }
        );

        // Auto-close dialog after 2 seconds
        setTimeout(() => {
          setRenewalDialog({ open: false, domain: '', type: '', status: 'processing' });
        }, 2000);
      } else {
        // Update dialog to error status
        setRenewalDialog(prev => ({ ...prev, status: 'error' }));

        // Enhanced failure notification
        const message = response.data.message || 'Simulasi pembaruan gagal';
        if (message.includes('rateLimited') ||
          message.includes('Rate limit') ||
          message.includes('Service busy')) {
          toast.error(
            <div className="flex flex-col gap-1">
              <div className="font-semibold">⏳ Rate Limit Terdeteksi</div>
              <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
              <div className="text-xs text-muted-foreground mt-1">
                <div>Let's Encrypt membatasi jumlah request.</div>
                <div className="font-medium mt-0.5">⏰ Tunggu 1-2 jam dan coba lagi.</div>
              </div>
            </div>,
            { duration: 6000 }
          );
        } else {
          toast.error(
            <div className="flex flex-col gap-1">
              <div className="font-semibold">❌ Simulasi Gagal</div>
              <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
              <div className="text-xs text-muted-foreground mt-1">
                Error: {message}
              </div>
            </div>,
            { duration: 5000 }
          );
        }

        // Auto-close dialog after 3 seconds
        setTimeout(() => {
          setRenewalDialog({ open: false, domain: '', type: '', status: 'processing' });
        }, 3000);
      }
    } catch (error) {
      // Update dialog to error status
      setRenewalDialog(prev => ({ ...prev, status: 'error' }));

      const errorMsg = error.response?.data?.message || error.message || 'Gagal melakukan simulasi pembaruan';
      const errorCode = error.response?.status;

      // Enhanced error notification with categorization
      if (errorMsg.includes('rateLimited') ||
        errorMsg.includes('Service busy') ||
        errorMsg.includes('Rate limit')) {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">⏳ Rate Limit Let's Encrypt</div>
            <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Terlalu banyak request dalam waktu singkat.</div>
              <div className="font-medium mt-0.5">⏰ Tunggu 1-2 jam lalu coba lagi.</div>
            </div>
          </div>,
          { duration: 6000 }
        );
      } else if (errorCode === 504) {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">⏱️ Request Timeout</div>
            <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Proses mungkin masih berjalan di server.</div>
              <div className="font-medium mt-0.5">Coba refresh halaman dalam beberapa saat.</div>
            </div>
          </div>,
          { duration: 6000 }
        );
      } else if (errorMsg.includes('DNS') || errorMsg.includes('domain')) {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">🌐 Masalah DNS/Domain</div>
            <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Error: {errorMsg}</div>
              <div className="font-medium mt-0.5">Periksa konfigurasi DNS domain Anda.</div>
            </div>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">❌ Simulasi Gagal</div>
            <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Error: {errorMsg}</div>
              {errorCode && <div>Kode: {errorCode}</div>}
            </div>
          </div>,
          { duration: 6000 }
        );
      }

      // Auto-close dialog after 3 seconds
      setTimeout(() => {
        setRenewalDialog({ open: false, domain: '', type: '', status: 'processing' });
      }, 3000);
    } finally {
      setLoading(prev => ({ ...prev, [`renew-${certId}`]: false }));
    }
  };


  const handleRenewLive = async (certId, domain) => {
    // Show the renewal process dialog
    setRenewalDialog({ open: true, domain, type: 'live', status: 'processing' });
    setLoading(prev => ({ ...prev, [`renew-live-${certId}`]: true }));

    try {
      const response = await certificatesAPI.renewLive(certId);

      // Update dialog to success status
      setRenewalDialog(prev => ({ ...prev, status: 'success' }));

      if (response.data.success) {
        // Enhanced success notification
        toast.success(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">🎉 Pembaruan Live Berhasil!</div>
            <div className="text-sm">
              Domain: <span className="font-medium">{domain}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {response.data.message || 'Sertifikat SSL telah diperbarui dan aktif. Data akan segera direfresh.'}
            </div>
          </div>,
          { duration: 6000 }
        );

        // Auto-close dialog after 2 seconds, then refresh
        setTimeout(() => {
          setRenewalDialog({ open: false, domain: '', type: '', status: 'processing' });
          onRefresh?.();
        }, 2000);
      } else {
        // Update dialog to error status
        setRenewalDialog(prev => ({ ...prev, status: 'error' }));

        // Enhanced failure notification
        const message = response.data.message || 'Pembaruan live gagal';
        if (message.includes('rateLimited') ||
          message.includes('Rate limit') ||
          message.includes('Service busy')) {
          toast.error(
            <div className="flex flex-col gap-1">
              <div className="font-semibold">⏳ Rate Limit Terdeteksi</div>
              <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
              <div className="text-xs text-muted-foreground mt-1">
                <div>Let's Encrypt membatasi jumlah pembaruan.</div>
                <div className="font-medium mt-0.5">⏰ Tunggu beberapa jam dan coba lagi.</div>
                <div className="mt-0.5">Tip: Lakukan Uji Pembaruan terlebih dahulu untuk menghindari rate limit.</div>
              </div>
            </div>,
            { duration: 7000 }
          );
        } else {
          toast.error(
            <div className="flex flex-col gap-1">
              <div className="font-semibold">❌ Pembaruan Live Gagal</div>
              <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
              <div className="text-xs text-muted-foreground mt-1">
                Error: {message}
              </div>
            </div>,
            { duration: 6000 }
          );
        }

        // Auto-close dialog after 3 seconds
        setTimeout(() => {
          setRenewalDialog({ open: false, domain: '', type: '', status: 'processing' });
        }, 3000);
      }
    } catch (error) {
      // Update dialog to error status
      setRenewalDialog(prev => ({ ...prev, status: 'error' }));

      const errorMsg = error.response?.data?.message || error.message || 'Gagal melakukan pembaruan live';
      const errorCode = error.response?.status;

      // Enhanced error notification with categorization
      if (errorMsg.includes('rateLimited') ||
        errorMsg.includes('Service busy') ||
        errorMsg.includes('Rate limit')) {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">⏳ Rate Limit Let's Encrypt</div>
            <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Terlalu banyak pembaruan dalam waktu singkat.</div>
              <div className="font-medium mt-0.5">⏰ Tunggu beberapa jam lalu coba lagi.</div>
              <div className="mt-0.5">💡 Tip: Gunakan Uji Pembaruan untuk validasi tanpa rate limit.</div>
            </div>
          </div>,
          { duration: 7000 }
        );
      } else if (errorCode === 504) {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">⏱️ Request Timeout</div>
            <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Proses pembaruan mungkin masih berjalan di background.</div>
              <div className="font-medium mt-0.5">Coba refresh halaman dalam beberapa menit.</div>
            </div>
          </div>,
          { duration: 7000 }
        );
      } else if (errorMsg.includes('DNS') || errorMsg.includes('domain')) {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">🌐 Masalah DNS/Domain</div>
            <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Error: {errorMsg}</div>
              <div className="font-medium mt-0.5">Periksa konfigurasi DNS dan pastikan domain dapat diakses.</div>
            </div>
          </div>,
          { duration: 7000 }
        );
      } else if (errorMsg.includes('validation') || errorMsg.includes('challenge')) {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">🔐 Gagal Validasi</div>
            <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Error: {errorMsg}</div>
              <div className="font-medium mt-0.5">Let's Encrypt tidak dapat memvalidasi kepemilikan domain.</div>
            </div>
          </div>,
          { duration: 7000 }
        );
      } else {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">❌ Pembaruan Live Gagal</div>
            <div className="text-sm">Domain: <span className="font-medium">{domain}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Error: {errorMsg}</div>
              {errorCode && <div>Kode: {errorCode}</div>}
            </div>
          </div>,
          { duration: 7000 }
        );
      }

      // Auto-close dialog after 3 seconds
      setTimeout(() => {
        setRenewalDialog({ open: false, domain: '', type: '', status: 'processing' });
      }, 3000);
    } finally {
      setLoading(prev => ({ ...prev, [`renew-live-${certId}`]: false }));
      setConfirmDialog({ open: false, certId: null, type: null });
    }
  };


  const openConfirmDialog = (certId, type) => {
    setConfirmDialog({ open: true, certId, type });
  };

  const handleConfirm = () => {
    const { certId, type } = confirmDialog;
    const cert = certificates.find(c => c.id === certId);
    if (type === 'live' && cert) {
      handleRenewLive(certId, cert.domain);
    }
  };

  const openInfoDialog = (cert) => {
    setInfoDialog({ open: true, cert });
  };

  // Render Mobile Card View with zebra striping
  const renderMobileCard = (cert, index) => {
    const daysRemaining = calculateDaysRemaining(cert.expiration_date);
    const renewalStatus = getRenewalStatus(daysRemaining);
    const renewalStatusText = getRenewalStatusText(renewalStatus);
    const renewalStatusColor = getRenewalStatusColor(renewalStatus);
    const renewalIcon = getRenewalStatusIcon(renewalStatus);
    const isEven = index % 2 === 0;

    return (
      <motion.div
        key={cert.id}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ scale: 1.01, y: -2 }}
        className="mb-2"
      >
        <Card className={`${isEven ? 'bg-muted/20' : 'bg-background'} transition-colors hover:shadow-md`}>
          <CardContent className="p-3 space-y-3">
            {/* Header - Domain & Status */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm sm:text-base truncate">{cert.domain}</h3>
                <div className="flex items-center gap-2 mt-1 text-xs sm:text-sm text-muted-foreground">
                  <Server className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="truncate">{cert.server_source}</span>
                </div>
              </div>
              <StatusBadge daysRemaining={daysRemaining} />
            </div>

            {/* Certificate Info */}
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between p-1.5 bg-muted/50 rounded-md">
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground text-xs sm:text-sm">Kedaluwarsa:</span>
                </div>
                <span className="font-medium text-xs sm:text-sm">{formatDate(cert.expiration_date)}</span>
              </div>

              <div className="flex items-center justify-between p-1.5 bg-muted/50 rounded-md">
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground text-xs sm:text-sm">Sisa Waktu:</span>
                </div>
                <span className={`font-semibold text-xs sm:text-sm ${daysRemaining < 0 ? 'text-gray-600' :
                  daysRemaining <= 7 ? 'text-red-600' :
                    daysRemaining <= 30 ? 'text-orange-600' :
                      'text-green-600'
                  }`}>
                  {daysRemaining < 0 ? 'Kedaluwarsa' : `${daysRemaining} hari`}
                </span>
              </div>

              <div className="flex items-center justify-between p-1.5 bg-muted/50 rounded-md">
                <span className="text-muted-foreground text-xs sm:text-sm">Info Pembaruan:</span>
                <div className="flex items-center gap-1.5">
                  <Badge className={`${renewalStatusColor} border text-xs whitespace-nowrap`}>
                    {renewalIcon} {renewalStatusText}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0"
                    onClick={() => openInfoDialog(cert)}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-1.5 pt-1.5 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownload(cert.id, cert.domain)}
                disabled={loading[`download-${cert.id}`]}
                className="w-full justify-center text-xs sm:text-sm h-8"
              >
                <Download className="h-3.5 w-3.5 mr-2" />
                {loading[`download-${cert.id}`] ? 'Mengunduh...' : 'Unduh Sertifikat'}
              </Button>

              <div className="grid grid-cols-2 gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRenewDryRun(cert.id, cert.domain)}
                  disabled={loading[`renew-${cert.id}`] || renewalStatus === 'safe'}
                  title={renewalStatus === 'safe' ? 'Sertifikat masih terlalu lama untuk uji pembaruan' : 'Uji pembaruan (dry-run)'}
                  className="w-full text-xs sm:text-sm h-8"
                >
                  <RefreshCw className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                  {loading[`renew-${cert.id}`] ? 'Menguji...' : 'Uji'}
                </Button>

                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => openConfirmDialog(cert.id, 'live')}
                  disabled={loading[`renew-live-${cert.id}`] || daysRemaining > 30}
                  title={daysRemaining > 30 ? 'Sertifikat masih > 30 hari' : 'Pembaruan live (production)'}
                  className="w-full text-xs sm:text-sm h-8"
                >
                  <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                  {loading[`renew-live-${cert.id}`] ? 'Memperbarui...' : 'Live'}
                </Button>
              </div>
            </div>

            {/* Last Updated */}
            <div className="text-xs text-muted-foreground text-center pt-1.5 border-t">
              Terakhir diperbarui: {formatDate(cert.last_updated)}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <>
      {/* Desktop Table View - Hidden on mobile with zebra striping */}
      <div className="hidden lg:block rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 h-10 border-b">
                <TableHead
                  className="font-semibold cursor-pointer hover:bg-muted/80 transition-colors w-[20%]"
                  onClick={() => requestSort('domain')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Domain</span>
                    {sortConfig.key === 'domain' && (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="font-semibold cursor-pointer hover:bg-muted/80 transition-colors w-[15%]"
                  onClick={() => requestSort('server_source')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Server</span>
                    {sortConfig.key === 'server_source' && (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="font-semibold cursor-pointer hover:bg-muted/80 transition-colors w-[15%]"
                  onClick={() => requestSort('expiration_date')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Expired</span>
                    {sortConfig.key === 'expiration_date' && (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="font-semibold cursor-pointer hover:bg-muted/80 transition-colors w-[20%]"
                  onClick={() => requestSort('days_remaining')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Sisa Waktu</span>
                    {sortConfig.key === 'days_remaining' && (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="font-semibold w-[10%]">Status</TableHead>
                <TableHead className="font-semibold w-[15%]">Info Pembaruan</TableHead>
                <TableHead className="text-right font-semibold w-[5%]">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCertificates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Tidak ada data sertifikat
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCertificates.map((cert, index) => {
                  const daysRemaining = calculateDaysRemaining(cert.expiration_date);
                  const renewalStatus = getRenewalStatus(daysRemaining);
                  const renewalStatusText = getRenewalStatusText(renewalStatus);
                  const renewalStatusColor = getRenewalStatusColor(renewalStatus);
                  const renewalIcon = getRenewalStatusIcon(renewalStatus);

                  // Progress Bar Logic
                  const maxDays = 90;
                  const progressValue = Math.max(0, Math.min(100, (daysRemaining / maxDays) * 100));
                  let progressColor = "bg-emerald-500";
                  if (daysRemaining <= 30) progressColor = "bg-orange-500";
                  if (daysRemaining <= 7) progressColor = "bg-red-500";

                  return (
                    <motion.tr
                      key={cert.id}
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className="hover:bg-muted/50 transition-colors border-b last:border-0 h-16"
                    >
                      <TableCell className="font-medium py-2">
                        <div className="flex flex-col">
                          <span className="font-semibold text-sm truncate max-w-[200px]" title={cert.domain}>{cert.domain}</span>
                          <span className="text-xs text-muted-foreground">ID: {cert.id}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Server className="h-3.5 w-3.5" />
                          <span className="truncate max-w-[150px]" title={cert.server_source}>{cert.server_source}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-sm whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          {formatDate(cert.expiration_date)}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="w-full max-w-[140px]">
                          <div className="flex justify-between text-xs mb-1">
                            <span className={`font-semibold ${daysRemaining < 0 ? 'text-gray-600' :
                              daysRemaining <= 7 ? 'text-red-600' :
                                daysRemaining <= 30 ? 'text-orange-600' :
                                  'text-emerald-600'
                              }`}>
                              {daysRemaining < 0 ? 'Expired' : `${daysRemaining} hari`}
                            </span>
                            <span className="text-muted-foreground">{progressValue.toFixed(0)}%</span>
                          </div>
                          <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full ${progressColor} transition-all duration-500 ease-out`}
                              style={{ width: `${progressValue}%` }}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <StatusBadge daysRemaining={daysRemaining} />
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`${renewalStatusColor} border text-[10px] py-0 px-2 h-5 whitespace-nowrap shadow-sm`}>
                            {renewalIcon} {renewalStatusText}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 rounded-full"
                            onClick={() => openInfoDialog(cert)}
                          >
                            <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-right py-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel>Aksi</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDownload(cert.id, cert.domain)} disabled={loading[`download-${cert.id}`]}>
                              <Download className="mr-2 h-4 w-4" />
                              {loading[`download-${cert.id}`] ? 'Mengunduh...' : 'Unduh'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleRenewDryRun(cert.id, cert.domain)} disabled={loading[`renew-${cert.id}`] || renewalStatus === 'safe'}>
                              <RefreshCw className="mr-2 h-4 w-4" />
                              {loading[`renew-${cert.id}`] ? 'Menguji...' : 'Uji Pembaruan'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => openConfirmDialog(cert.id, 'live')}
                              disabled={loading[`renew-live-${cert.id}`] || daysRemaining > 30}
                              className="text-red-600 focus:text-red-600 focus:bg-red-50"
                            >
                              <AlertTriangle className="mr-2 h-4 w-4" />
                              {loading[`renew-live-${cert.id}`] ? 'Memperbarui...' : 'Pembaruan Live'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </motion.tr>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between px-4 py-4 bg-muted/20 border-t">
            <div className="text-sm text-muted-foreground">
              Halaman {currentPage} dari {totalPages || 1}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronDown className="h-4 w-4 rotate-90" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p = i + 1;
                  if (totalPages > 5 && currentPage > 3) {
                    p = currentPage - 2 + i;
                  }
                  if (p > totalPages) return null;

                  return (
                    <Button
                      key={p}
                      variant={currentPage === p ? "default" : "outline"}
                      size="sm"
                      className={`h-8 w-8 p-0 ${currentPage === p ? 'bg-primary text-primary-foreground' : ''}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="h-8 w-8 p-0"
              >
                <ChevronDown className="h-4 w-4 -rotate-90" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Card View - Visible only on mobile with zebra striping */}
      <div className="lg:hidden space-y-2">
        {certificates.length === 0 ? (
          <Card>
            <CardContent className="text-center text-muted-foreground py-8">
              Tidak ada data sertifikat
            </CardContent>
          </Card>
        ) : (
          certificates.map((cert, index) => renderMobileCard(cert, index))
        )}
      </div>

      {/* Info Dialog */}
      <Dialog open={infoDialog.open} onOpenChange={(open) => setInfoDialog({ ...infoDialog, open })}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Informasi Status Pembaruan</DialogTitle>
            <DialogDescription>
              Detail rekomendasi pembaruan untuk {infoDialog.cert?.domain}
            </DialogDescription>
          </DialogHeader>
          {infoDialog.cert && (
            <div className="space-y-4">
              <div className="p-4 border rounded-lg bg-muted/50">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Domain:</span>
                    <span className="truncate ml-2 text-right">{infoDialog.cert.domain}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Server:</span>
                    <span className="truncate ml-2 text-right">{infoDialog.cert.server_source}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Sisa Waktu:</span>
                    <span className="font-semibold">
                      {calculateDaysRemaining(infoDialog.cert.expiration_date)} hari
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Tanggal Kedaluwarsa:</span>
                    <span className="text-right">{formatDate(infoDialog.cert.expiration_date)}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                <h4 className="font-semibold text-sm text-blue-900 mb-2">
                  {getRenewalStatusIcon(getRenewalStatus(calculateDaysRemaining(infoDialog.cert.expiration_date)))}
                  {' '}{getRenewalStatusText(getRenewalStatus(calculateDaysRemaining(infoDialog.cert.expiration_date)))}
                </h4>
                <p className="text-sm text-blue-800">
                  {getRenewalActionRecommendation(calculateDaysRemaining(infoDialog.cert.expiration_date))}
                </p>
              </div>

              <div className="text-xs text-muted-foreground space-y-1">
                <p>📅 <strong>Timeline Pembaruan:</strong></p>
                <ul className="list-disc list-inside ml-2 space-y-0.5">
                  <li>Hari 0-60: Aman, belum perlu pembaruan</li>
                  <li>Hari 60-82: Boleh uji pembaruan</li>
                  <li>Hari 83-89: Sudah bisa pembaruan live</li>
                  <li>Hari 90+: Kedaluwarsa!</li>
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <AlertDialogContent className="max-w-md mx-4 sm:mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Pembaruan Live</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin melakukan <strong>PEMBARUAN LIVE</strong> untuk sertifikat ini?
              <br /><br />
              <span className="text-red-600 font-semibold">
                ⚠️ Tindakan ini akan melakukan pembaruan sesungguhnya ke Let's Encrypt!
              </span>
              <br /><br />
              <span className="text-sm text-muted-foreground">
                Pastikan sertifikat memang perlu diperbarui (kurang dari 30 hari).
                Pembaruan yang tidak perlu dapat menyebabkan rate limit.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} className="w-full sm:w-auto bg-red-600 hover:bg-red-700">
              Ya, Lakukan Pembaruan Live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Renewal Process Dialog */}
      <RenewalProcessDialog
        open={renewalDialog.open}
        onOpenChange={(open) => {
          if (!open && renewalDialog.status !== 'processing') {
            setRenewalDialog({ open: false, domain: '', type: '', status: 'processing' });
          }
        }}
        domain={renewalDialog.domain}
        type={renewalDialog.type}
        status={renewalDialog.status}
      />
    </>
  );
}